import { type ReactNode, useMemo } from 'react';

type Mapping<T> = {
  name: string | ReactNode;
  dataKey: keyof T;
  color: string;
  id: string;
  detailsDataKey?: keyof T;
};

type ConnectedMappings<T> = (Mapping<T> & {
  prev: Record<string, never> | Mapping<T>;
})[];

const useStacked = <T>(
  data: T[],
  mappings: Mapping<T>[],
): [Record<keyof T, number>[], ConnectedMappings<T>] =>
  useMemo(() => {
    const connectedMappings: ConnectedMappings<T> = mappings.map((curr, i) => ({
      ...curr,
      prev: mappings[i - 1] || {},
    }));

    const stackedData = data.map(dataPoint =>
      connectedMappings.reduce(
        (acc, mapping) => {
          const value = Number(dataPoint[mapping.dataKey]) || 0;
          const from = Number(acc[mapping.prev.dataKey]) || 0;
          return { ...acc, [mapping.dataKey]: from + value };
        },
        {} as Record<keyof T, number>,
      ),
    );
    return [stackedData, connectedMappings];
  }, [data, mappings]);

export default useStacked;
